// ---------------------------------------------------------------------------
// ApiErrorAlert — the standard way this app surfaces an API failure.
//
// Renders a caught (axios) error as a `role="alert"` MUI Alert: the
// extracted {message} plus, when the ADR-0013 envelope carries one, the
// {requestId} as a muted reference caption (so a user can quote it in a
// support ticket). Pass the RAW caught error (e.g. a react-query
// `mutation.error`); a null/undefined error renders nothing, so it can sit
// unconditionally in a dialog/page and only appear on failure.
//
// Centralizes what was previously scattered, inconsistent inline error
// rendering (some pages showed `error.response?.data?.message`, many showed
// nothing). Mirrors the admin-app / app-vectros ApiErrorAlert in plain JS.
// ---------------------------------------------------------------------------

import React from 'react';
import { Alert, AlertTitle, Typography } from '@mui/material';

import { getApiErrorMessage, getApiRequestId } from '../api/apiError';

/**
 * @param {object} props
 * @param {unknown} props.error - the caught error, or null/undefined to render nothing
 * @param {() => void} [props.onClose] - when set, renders a dismiss affordance
 * @param {React.ReactNode} [props.title] - optional AlertTitle
 * @param {object} [props.sx] - MUI sx passthrough
 */
export default function ApiErrorAlert({ error, onClose, title, sx }) {
  if (!error) return null;
  const message = getApiErrorMessage(error);
  const requestId = getApiRequestId(error);
  return (
    <Alert severity="error" role="alert" onClose={onClose} sx={sx}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
      {requestId && (
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 0.5, fontFamily: 'monospace', opacity: 0.8 }}
        >
          Reference ID: {requestId}
        </Typography>
      )}
    </Alert>
  );
}
